import type en from '../en/admin'
import type { Translation } from '../types'

/** Platform administration */
const admin: Translation<typeof en> = {
  "admin.module.webhooks.label": "Webhooks",
  "admin.module.webhooks.desc": "Uitgaande HTTP-webhooks bij gebeurtenissen",
  "admin.module.branding.label": "Huisstijl",
  "admin.module.branding.desc": "Eigen logo, kleuren, white label",
  "admin.module.apiKeys.label": "API-sleutels",
  "admin.module.apiKeys.desc": "Toegang tot de API voor scripts en andere systemen",
  "admin.module.templates.label": "Sjablonen",
  "admin.module.templates.desc": "Sjablonen en wizard voor netwerkconfiguratie",
  "admin.module.customDomain.label": "Eigen domein",
  "admin.module.customDomain.desc": "Inloggen op een eigen adres van het bedrijf",
  "admin.module.customEmail.label": "Eigen mailserver",
  "admin.module.customEmail.desc": "SMTP per bedrijf",
  "admin.module.sso.label": "Single sign-on",
  "admin.module.sso.desc": "Inloggen via de identiteitsprovider van het bedrijf (OpenID Connect)",
  "admin.module.ownWhatsapp.label": "Eigen WhatsApp",
  "admin.module.ownWhatsapp.desc": "Het eigen Meta- of Twilio-account van het bedrijf",
  "admin.module.ownRcs.label": "Eigen RCS",
  "admin.module.ownRcs.desc": "De eigen RCS-afzender van het bedrijf (Google of Twilio)",
  "admin.module.linkedWhatsapp.label": "Gekoppeld WhatsApp-nummer",
  "admin.module.linkedWhatsapp.desc": "Het bedrijf koppelt zijn eigen WhatsApp-nummer aan de server (open-wa)",
  "admin.module.unifi.label": "UniFi",
  "admin.module.unifi.desc": "UniFi-controllers en Site Manager",
  "admin.module.teltonika.label": "Teltonika",
  "admin.module.teltonika.desc": "RutOS-apparaten en RMS",
  "admin.module.peplink.label": "Peplink",
  "admin.module.peplink.desc": "Peplink-apparaten en InControl2",
  "admin.module.mikrotik.label": "MikroTik",
  "admin.module.mikrotik.desc": "RouterOS-routers, accesspoints en straalverbindingen",
  "admin.module.velanox.label": "Velanox One Speed",
  "admin.module.velanox.desc": "Velanox-boxen en endpoints",
  "admin.overview.title": "Platformoverzicht",
  "admin.overview.subtitle": "Dashboard van het platformbeheer",
  "admin.overview.companies": "Bedrijven",
  "admin.overview.users": "Gebruikers",
  "admin.overview.sites": "Locaties",
  "admin.overview.pendingJobs": "Wachtende taken",
  "admin.overview.revocations": "Intrekkingen van certificaten die opnieuw worden geprobeerd",
  "admin.overview.revocationsText": "Deze VPN-certificaten konden nog niet worden ingetrokken en werken dus nog. Elk wordt opnieuw geprobeerd (hooguit een uur ertussen) tot het lukt.",
  "admin.overview.siteCert": "Certificaat van een locatie",
  "admin.overview.userCert": "Certificaat van een gebruiker",
  "admin.overview.tries": { one: "{count} poging · volgende poging {when}", other: "{count} pogingen · volgende poging {when}" },
  "admin.overview.byStatus": "Bedrijven per status",
  "admin.overview.jobs": "Takenwachtrij",
  "admin.overview.all": "Alle",
  "admin.overview.refreshJobs": "Taken vernieuwen",
  "admin.overview.noJobs": "Geen taken",
  "admin.overview.audit": "Recent auditlogboek",
  "admin.overview.noAudit": "Geen regels in het auditlogboek",
  "admin.overview.system": "Systeem",
  "admin.vpn.title": "VPN-beheer",
  "admin.vpn.subtitle": "OpenVPN-servers, de netwerken en accounts van elk bedrijf",
  "admin.vpn.refresh": "Vernieuwen",
  "admin.vpn.server": "Server",
  "admin.vpn.running": "Actief",
  "admin.vpn.stopped": "Gestopt",
  "admin.vpn.since": "Sinds {when}",
  "admin.vpn.connectedNow": "Nu verbonden",
  "admin.vpn.ofActive": { one: "van {count} actief account", other: "van {count} actieve accounts" },
  "admin.vpn.totalAccounts": "Accounts in totaal",
  "admin.vpn.acrossCompanies": { one: "verdeeld over {count} bedrijf", other: "verdeeld over {count} bedrijven" },
  "admin.vpn.networks": "Netwerken",
  "admin.vpn.isolated": "gescheiden /24-subnetten",
  "admin.vpn.currentlyConnected": "Nu verbonden ({count})",
  "admin.vpn.commonName": "Common name",
  "admin.vpn.realIp": "Echt IP-adres",
  "admin.vpn.sinceCol": "Sinds",
  "admin.vpn.usersSites": "Gebruikers: {users} · Locaties: {sites}",
  "admin.vpn.connectedOf": { one: "{connected} verbonden / {count} account", other: "{connected} verbonden / {count} accounts" },
  "admin.vpn.noAccounts": "Nog geen VPN-accounts",
  "admin.vpn.name": "Naam",
  "admin.vpn.logs": "OpenVPN-logboeken",
  "admin.vpn.logLines": "Logregels",
  "admin.vpn.lines": { one: "{count} regel", other: "{count} regels" },
  "admin.vpn.refreshLogs": "Logboeken vernieuwen",
  "admin.vpn.hide": "Verbergen",
  "admin.vpn.showLogs": "Logboeken tonen",
  "admin.vpn.loadHint": "Kies \"Logboeken tonen\" om ze te laden",
  "admin.plans.toggleFailed": "Niet gewijzigd",
  "admin.plans.deleted": "Plan verwijderd",
  "admin.plans.deleteFailed": "Het plan kan niet worden verwijderd — er zijn mogelijk actieve licenties",
  "admin.plans.title": "Licentieplannen",
  "admin.plans.subtitle": "Herbruikbare licentietypen en de modules die ze ontgrendelen",
  "admin.plans.new": "Nieuw plan",
  "admin.plans.deleteConfirm": "Plan \"{name}\" verwijderen? Bedrijven die het gebruiken, houden hun huidige licentie.",
  "admin.plans.empty": "Nog geen licentieplannen — maak er een aan om het aan bedrijven te geven",
  "admin.plans.createTitle": "Licentieplan aanmaken",
  "admin.plans.created": "Plan aangemaakt",
  "admin.plans.editTitle": "Bewerken: {name}",
  "admin.plans.updated": "Plan bijgewerkt",
  "admin.plans.public": "Openbaar",
  "admin.plans.activeCount": { one: "{count} actief", other: "{count} actief" },
  "admin.plans.editNamed": "{name} bewerken",
  "admin.plans.deleteNamed": "{name} verwijderen",
  "admin.plans.sites": "Locaties",
  "admin.plans.users": "Gebruikers",
  "admin.plans.duration": "Looptijd",
  "admin.plans.daysShort": "{count} d",
  "admin.plans.modules": "Modules",
  "admin.plans.nameRequired": "Een naam is verplicht",
  "admin.plans.saveFailed": "Het plan kon niet worden opgeslagen",
  "admin.plans.name": "Naam van het plan *",
  "admin.plans.namePlaceholder": "bijv. Professional",
  "admin.plans.description": "Beschrijving",
  "admin.plans.descriptionPlaceholder": "Optionele beschrijving",
  "admin.plans.limits": "Limieten",
  "admin.plans.limitsHint": "(leeg laten voor onbeperkt)",
  "admin.plans.maxSites": "Maximaal aantal locaties",
  "admin.plans.maxUsers": "Maximaal aantal gebruikers",
  "admin.plans.durationDays": "Looptijd (dagen)",
  "admin.plans.included": "Inbegrepen modules",
  "admin.plans.selfSignup": "Beschikbaar bij zelf aanmelden",
  "admin.plans.selfSignupHint": "Dit plan tonen op de registratiepagina",
  "admin.plans.save": "Plan opslaan",
  "admin.admins.removed": "Globale beheerder verwijderd",
  "admin.admins.removeFailed": "Verwijderen mislukt",
  "admin.admins.reset2faDone": "Tweestapsaanmelding teruggezet",
  "admin.admins.reset2faText": "De persoon wordt overal uitgelogd en stelt het opnieuw in bij de volgende keer inloggen.",
  "admin.admins.resetFailed": "Terugzetten mislukt",
  "admin.admins.title": "Globale beheerders",
  "admin.admins.subtitle": "Mensen met rechten als globale beheerder: platformbeheer voor elk bedrijf",
  "admin.admins.add": "Globale beheerder toevoegen",
  "admin.admins.warning": "Globale beheerders hebben volledige toegang tot elk bedrijf, elke instelling en alle gegevens op het platform. Geef dit alleen aan vertrouwde medewerkers.",
  "admin.admins.name": "Naam",
  "admin.admins.email": "E-mail",
  "admin.admins.role": "Rol",
  "admin.admins.lastLogin": "Laatst ingelogd",
  "admin.admins.created": "Aangemaakt",
  "admin.admins.empty": "Geen globale beheerders gevonden",
  "admin.admins.you": "(u)",
  "admin.admins.owner": "Eigenaar",
  "admin.admins.superAdmin": "Superbeheerder",
  "admin.admins.admin": "Beheerder",
  "admin.admins.invited": "Uitgenodigd",
  "admin.admins.never": "Nooit",
  "admin.admins.edit": "Bewerken",
  "admin.admins.editNamed": "{name} bewerken",
  "admin.admins.reset2fa": "Tweestapsaanmelding terugzetten",
  "admin.admins.reset2faFor": "Tweestapsaanmelding terugzetten voor {name}",
  "admin.admins.reset2faConfirm": "Tweestapsaanmelding terugzetten voor \"{name}\"? De persoon wordt overal uitgelogd en stelt het opnieuw in bij de volgende keer inloggen.",
  "admin.admins.ownerFixed": "De eigenaar van het platform kan niet worden verwijderd",
  "admin.admins.notSelf": "U kunt uw eigen rechten niet verwijderen",
  "admin.admins.remove": "Verwijderen",
  "admin.admins.removeNamed": "{name} verwijderen als globale beheerder",
  "admin.admins.removeConfirm": "\"{name}\" ({email}) verwijderen als globale beheerder? Een account dat bij bedrijven hoort, houdt die; een account zonder bedrijf wordt verwijderd.",
  "admin.admins.ownHint": "Uw eigen naam, wachtwoord en tweestapsaanmelding staan onder",
  "admin.admins.invitationSent": "Uitnodiging verstuurd",
  "admin.admins.invitationText": "{email} stelt via de mail een wachtwoord in en daarna tweestapsaanmelding.",
  "admin.admins.added": "Globale beheerder toegevoegd",
  "admin.admins.addedText": "Dit account heeft nu rechten als globale beheerder.",
  "admin.admins.editTitle": "Bewerken: {name}",
  "admin.admins.updated": "Beheerder bijgewerkt",
  "admin.admins.required": "Een naam en een e-mailadres zijn verplicht",
  "admin.admins.saveFailed": "Opslaan mislukt",
  "admin.admins.fullName": "Volledige naam *",
  "admin.admins.emailAddress": "E-mailadres *",
  "admin.admins.emailAddressPlain": "E-mailadres",
  "admin.admins.emailHint": "Een bestaand account krijgt de rechten. Een nieuw adres krijgt een mail om een wachtwoord in te stellen.",
  "admin.admins.emailFixed": "Het e-mailadres kan niet worden gewijzigd",
  "admin.admins.superAdminHint": "Kan andere globale beheerders en voor het platform kritieke instellingen beheren",
  "admin.admins.saveChanges": "Wijzigingen opslaan",
  "admin.admins.sendInvitation": "Uitnodiging versturen",
  "admin.settings.provider": "Provider",
  "admin.settings.serverSettings": "Serverinstellingen gebruiken (.env)",
  "admin.settings.off": "Uit",
  "admin.settings.waHint": "Zodra hier Meta of Twilio is gekozen, tellen alleen de waarden op deze pagina: er wordt niets uit de instellingen van de server overgenomen. Opgeslagen wijzigingen gelden direct, zonder herstart. Tokens worden versleuteld opgeslagen en nooit meer getoond. Bedrijven met de licentiemodule Eigen WhatsApp kunnen in plaats daarvan hun eigen verbinding gebruiken (Bedrijfsinstellingen → WhatsApp).",
  "admin.settings.changedProvider": "U hebt de provider gewijzigd: sla de instellingen op om hem te gebruiken.",
  "admin.settings.rcsHint": "RCS-berichten komen aan in de berichtenapp van de telefoon zelf, onder uw geverifieerde bedrijfsnaam. Zodra hier Google of Twilio is gekozen, tellen alleen de waarden op deze pagina. Opgeslagen wijzigingen gelden direct. Sleutels en tokens worden versleuteld opgeslagen en nooit meer getoond. Bedrijven met de licentiemodule Eigen RCS kunnen in plaats daarvan hun eigen afzender gebruiken (Bedrijfsinstellingen → RCS).",
  "admin.settings.templatesChecked": "Sjablonen gecontroleerd",
  "admin.settings.syncFailed": "Synchroniseren mislukt",
  "admin.settings.tryAgain": "Probeer het zo opnieuw.",
  "admin.settings.templatesOff": "Wanneer WhatsApp via Twilio loopt (hierboven gekozen of in de instellingen van de server), maakt het platform de sjablonen zelf en kunnen bedrijven hun teksten wijzigen. Bij Meta worden de sjablonen gebruikt die in de instellingen zijn genoemd.",
  "admin.settings.templatesIntro": "WhatsApp keurt elke tekst goed voordat die kan worden gebruikt. Nieuwe teksten worden elke 30 minuten ingediend en uitspraken opgehaald; tot de eigen tekst van een bedrijf is goedgekeurd, gaat de standaardtekst.",
  "admin.settings.templatesInUse": { one: "{count} sjabloon in gebruik. WhatsApp staat er {unverified} per zakelijk account toe, of {verified} zodra het bedrijf bij Meta is geverifieerd. De eigen WhatsApp-tekst van elk bedrijf is een eigen sjabloon.", other: "{count} sjablonen in gebruik. WhatsApp staat er {unverified} per zakelijk account toe, of {verified} zodra het bedrijf bij Meta is geverifieerd. De eigen WhatsApp-tekst van elk bedrijf is een eigen sjabloon." },
  "admin.settings.nearLimit": "Dicht bij de limiet van een niet-geverifieerd zakelijk account ({limit}). Verifieer het bedrijf bij Meta, anders kunnen nieuwe teksten niet meer worden goedgekeurd zodra die is bereikt.",
  "admin.settings.message": "Bericht",
  "admin.settings.text": "Tekst",
  "admin.settings.usedBy": "Gebruikt door",
  "admin.settings.authMessage": "Het eigen bericht van WhatsApp met de verificatiecode",
  "admin.settings.usedBy.default": "Standaard",
  "admin.settings.usedBy.previousDefault": "Vorige standaardtekst, tot de nieuwe tekst is goedgekeurd",
  "admin.settings.notUsed": "Niet meer in gebruik",
  "admin.settings.wa.approved": "goedgekeurd",
  "admin.settings.wa.rejected": "afgewezen",
  "admin.settings.wa.failed": "niet ingediend",
  "admin.settings.wa.paused": "gepauzeerd",
  "admin.settings.wa.disabled": "uitgeschakeld",
  "admin.settings.wa.pending": "wacht",
  "admin.settings.noTemplates": "Nog geen: de eerste synchronisatie maakt ze aan.",
  "admin.settings.syncNow": "Nu synchroniseren",
  "admin.settings.testSent": "Testmail verstuurd",
  "admin.settings.checkInbox": "Kijk in {to}",
  "admin.settings.smtpFailed": "SMTP-test mislukt",
  "admin.settings.save": "Instellingen opslaan",
  "admin.settings.title": "Platforminstellingen",
  "admin.settings.subtitle": "Algemeen gedrag, e-mail, WhatsApp, RCS en VPN",
  "admin.settings.tab": "Instellingen",
  "admin.settings.edgeTab": "Edge-proxy (Caddy + HAProxy) →",
  "admin.settings.loading": "Laden",
  "admin.settings.behaviour": "Gedrag van het platform",
  "admin.settings.licensing": "Licenties aan",
  "admin.settings.licensingHint": "Licentielimieten handhaven. Uitschakelen voor onbeperkte toegang.",
  "admin.settings.maintenance": "Onderhoudsmodus",
  "admin.settings.maintenanceHint": "Voorkomt dat gebruikers van bedrijven inloggen. Globale beheerders houden toegang.",
  "admin.settings.selfRegistration": "Zelf registreren toestaan",
  "admin.settings.selfRegistrationHint": "Laat bedrijven zich aanmelden zonder uitnodiging van een globale beheerder.",
  "admin.settings.adminIdle": "Globale beheerders: uitloggen na een periode zonder activiteit, hooguit",
  "admin.settings.adminIdleHint": "Standaard 1 uur; elke beheerder kan onder Mijn profiel een kortere tijd kiezen.",
  "admin.settings.idleOff": "Beheerders mogen uitloggen na inactiviteit uitschakelen",
  "admin.settings.idleOffHint": "Voor hun eigen account.",
  "admin.settings.idleOffWarning": "Hiermee kunnen globale beheerders een van de accountbeveiligingen van het platform uitschakelen: een beheerderssessie die openstaat op een onbeheerde computer blijft dan ingelogd tot zij haar maximale duur bereikt.",
  "admin.settings.idleOffQuestion": "Toestaan?",
  "admin.settings.defaultLicense": "Standaard licentietype",
  "admin.settings.licenseType.TRIAL": "Proef",
  "admin.settings.licenseType.STANDARD": "Standaard",
  "admin.settings.licenseType.UNLIMITED": "Onbeperkt",
  "admin.settings.licenseType.CUSTOM": "Op maat",
  "admin.settings.trialDays": "Standaard aantal proefdagen",
  "admin.settings.email": "E-mail / SMTP",
  "admin.settings.emailIntro": "Standaarden voor het hele platform. Bedrijven kunnen in hun bedrijfsinstellingen hun eigen SMTP gebruiken. Wachtwoorden worden vóór opslag versleuteld met AES-256.",
  "admin.settings.smtpHost": "SMTP-host",
  "admin.settings.smtpPort": "SMTP-poort",
  "admin.settings.username": "Gebruikersnaam",
  "admin.settings.password": "Wachtwoord",
  "admin.settings.saved": "✓ opgeslagen",
  "admin.settings.keepPassword": "(leeg laten om het huidige te houden)",
  "admin.settings.enterPassword": "SMTP-wachtwoord",
  "admin.settings.hidePassword": "Wachtwoord verbergen",
  "admin.settings.showPassword": "Wachtwoord tonen",
  "admin.settings.fromEmail": "Afzenderadres",
  "admin.settings.fromName": "Afzendernaam",
  "admin.settings.sendTest": "Testmail versturen",
  "admin.settings.testRecipient": "Ontvanger van de test",
  "admin.settings.testRecipientPlaceholder": "uw@e-mail.nl",
  "admin.settings.send": "Test versturen",
  "admin.settings.waTemplates": "WhatsApp-sjablonen",
  "admin.settings.vpn": "VPN / OpenVPN",
  "admin.settings.vpnIntro": "Bepaalt de VPN voor nieuwe bedrijven. Elk bedrijf krijgt een eigen /24-subnet.",
  "admin.settings.serverHost": "Hostnaam / IP van de server",
  "admin.settings.usersBase": "Basis van het gebruikersnetwerk",
  "admin.settings.sitesBase": "Basis van het locatienetwerk",
  "admin.settings.usersExample": "Bedrijf 1 → 10.8.1.0/24 · Bedrijf 2 → 10.8.2.0/24 …",
  "admin.settings.sitesExample": "Bedrijf 1 → 10.9.1.0/24 · Bedrijf 2 → 10.9.2.0/24 …",
  "admin.licences.deleted": "Licentie verwijderd",
  "admin.licences.deleteFailed": "Verwijderen mislukt",
  "admin.licences.notified": "E-mail verstuurd naar de beheerders van het bedrijf",
  "admin.licences.notifyFailed": "De e-mail kon niet worden verstuurd",
  "admin.licences.keyRevoked": "Sleutel ingetrokken",
  "admin.licences.revokeFailed": "Intrekken mislukt",
  "admin.licences.unusedDeleted": "Ongebruikte sleutels verwijderd",
  "admin.licences.expiredDeleted": "Verlopen licenties verwijderd",
  "admin.licences.bulkFailed": "Verwijderen mislukt",
  "admin.licences.tab.used": "Actief",
  "admin.licences.tab.available": "Sleutels",
  "admin.licences.tab.expired": "Verlopen",
  "admin.licences.title": "Licentieoverzicht",
  "admin.licences.subtitle": "Elke licentie en sleutel die op het platform is uitgegeven",
  "admin.licences.generate": "Sleutel genereren",
  "admin.licences.activeCount": "Actieve licenties",
  "admin.licences.availableCount": "Beschikbare sleutels",
  "admin.licences.expiredCount": "Verlopen licenties",
  "admin.licences.totalKeys": "Uitgegeven sleutels in totaal",
  "admin.licences.company": "Bedrijf",
  "admin.licences.plan": "Plan",
  "admin.licences.started": "Gestart",
  "admin.licences.expires": "Verloopt",
  "admin.licences.limits": "Limieten",
  "admin.licences.noActive": "Geen actieve licenties",
  "admin.licences.daysLeft": { one: "nog {count} dag", other: "nog {count} dagen" },
  "admin.licences.unlimited": "Onbeperkt",
  "admin.licences.sites": { one: "{count} locatie", other: "{count} locaties" },
  "admin.licences.sitesUnlimited": "∞ locaties",
  "admin.licences.users": { one: "{count} gebruiker", other: "{count} gebruikers" },
  "admin.licences.usersUnlimited": "∞ gebruikers",
  "admin.licences.thisCompany": "dit bedrijf",
  "admin.licences.deleteFor": "De licentie van {name} verwijderen",
  "admin.licences.deleteConfirm": "De licentie van \"{name}\" verwijderen?",
  "admin.licences.deleteUnusedConfirm": { one: "De ongebruikte sleutel verwijderen?", other: "Alle {count} ongebruikte sleutels verwijderen?" },
  "admin.licences.deleteUnused": "Alle ongebruikte verwijderen",
  "admin.licences.available": "Beschikbaar ({count})",
  "admin.licences.key": "Sleutel",
  "admin.licences.restriction": "Beperking",
  "admin.licences.created": "Aangemaakt",
  "admin.licences.redeemed": "Ingewisseld ({count})",
  "admin.licences.usedBy": "Gebruikt door",
  "admin.licences.redeemedAt": "Ingewisseld",
  "admin.licences.noKeys": "Nog geen licentiesleutels — genereer ze hierboven",
  "admin.licences.deleteExpiredConfirm": { one: "De verlopen licentie verwijderen?", other: "Alle {count} verlopen licenties verwijderen?" },
  "admin.licences.deleteExpired": "Alle verlopen verwijderen",
  "admin.licences.expiredOn": "Verlopen",
  "admin.licences.durationWas": "Looptijd was",
  "admin.licences.noExpired": "Geen verlopen licenties",
  "admin.licences.days": { one: "{count} dag", other: "{count} dagen" },
  "admin.licences.notifyHint": "De beheerders van het bedrijf mailen over het verlopen",
  "admin.licences.notify": "Melden",
  "admin.licences.deleteExpiredFor": "De verlopen licentie van {name} verwijderen",
  "admin.licences.deleteExpiredOne": "De verlopen licentie van \"{name}\" verwijderen?",
  "admin.licences.copyKey": "De volledige sleutel kopiëren",
  "admin.licences.anyPlan": "Elk",
  "admin.licences.companyOnly": "🏢 Alleen één bedrijf",
  "admin.licences.noRestriction": "🌐 Onbeperkt",
  "admin.licences.never": "Nooit",
  "admin.licences.revokeKey": "Sleutel intrekken",
  "admin.licences.revokeConfirm": "Deze sleutel intrekken?",
  "admin.licences.generated": { one: "{count} sleutel gegenereerd", other: "{count} sleutels gegenereerd" },
  "admin.licences.generateFailed": "Genereren mislukt",
  "admin.licences.generateTitle": { one: "Licentiesleutel genereren", other: "Licentiesleutels genereren" },
  "admin.licences.sendKeys": "Kopieer deze sleutels en stuur ze naar uw klanten. Zij kunnen ze inwisselen bij het aanmelden of in hun accountinstellingen.",
  "admin.licences.copy": "Kopiëren",
  "admin.licences.allCopied": "Alle sleutels gekopieerd",
  "admin.licences.copyAll": "Alles kopiëren",
  "admin.licences.done": "Klaar",
  "admin.licences.licencePlan": "Licentieplan",
  "admin.licences.anyPlanOption": "Elk plan (geen beperking)",
  "admin.licences.restrict.unlimited": "🌐 Onbeperkt",
  "admin.licences.restrict.email": "📧 E-mail",
  "admin.licences.restrict.company": "🏢 Bedrijf",
  "admin.licences.limitEmail": "Beperken tot e-mailadres",
  "admin.licences.limitCompany": "Beperken tot bedrijf",
  "admin.licences.chooseCompany": "Kies een bedrijf…",
  "admin.licences.count": "Aantal sleutels",
  "admin.licences.keyExpires": "Sleutel verloopt",
  "admin.licences.optional": "(optioneel)",
  "admin.licences.notes": "Notities",
  "admin.licences.notesPlaceholder": "Interne notitie over deze reeks sleutels",
  "admin.licences.generateCount": { one: "Sleutel genereren", other: "{count} sleutels genereren" },
  "admin.edge.resolves": "{domain} verwijst correct",
  "admin.edge.dnsNotHere": "De DNS verwijst nog niet hierheen",
  "admin.edge.resolvesTo": "Verwijst naar {ips}",
  "admin.edge.noARecord": "Geen A-record gevonden",
  "admin.edge.proven": "Eigendom bewezen",
  "admin.edge.servesNow": "Het domein toont nu het dashboard.",
  "admin.edge.notProvenYet": "Nog niet bewezen",
  "admin.edge.markedProven": "Gemarkeerd als bewezen",
  "admin.edge.ownershipReset": "Eigendom teruggezet",
  "admin.edge.resetText": "Het domein toont de inrichtingspagina tot het nieuwe TXT-record is gevonden.",
  "admin.edge.failed": "Mislukt",
  "admin.edge.requestFailed": "Het verzoek is mislukt",
  "admin.edge.removed": "Domein verwijderd",
  "admin.edge.caddyApplied": "Caddy-configuratie toegepast",
  "admin.edge.caddyRejected": "Caddy heeft de configuratie geweigerd",
  "admin.edge.title": "Edge-proxy",
  "admin.edge.subtitle": "HAProxy vóór :80/:443 · Caddy beëindigt TLS en geeft certificaten op aanvraag uit · OpenVPN per bedrijf via UDP",
  "admin.edge.addDomain": "Domein toevoegen",
  "admin.edge.connections": { one: "{count} verbinding", other: "{count} verbindingen" },
  "admin.edge.notReachable": "Niet bereikbaar",
  "admin.edge.onDemandOn": "TLS op aanvraag actief",
  "admin.edge.onDemandOff": "TLS op aanvraag NIET ingesteld",
  "admin.edge.edgeIp": "Edge-IP",
  "admin.edge.noEdgeIp": "Niet ingesteld — klanten kan niet worden verteld waar hun DNS naartoe moet wijzen",
  "admin.edge.acme": "ACME: {email}",
  "admin.edge.noAcme": "Geen ACME-e-mailadres ingesteld",
  "admin.edge.caddyWarnBefore": "Caddy draait, maar TLS op aanvraag is niet gekoppeld aan het ask-endpoint. White-label-domeinen krijgen geen certificaten tot u de configuratie toepast — gebruik ",
  "admin.edge.caddyWarnAfter": " onder Configuratie.",
  "admin.edge.tab.domains": "Domeinen ({count})",
  "admin.edge.tab.vpn": "VPN-instanties",
  "admin.edge.tab.config": "Configuratie",
  "admin.edge.domain": "Domein",
  "admin.edge.type": "Type",
  "admin.edge.company": "Bedrijf",
  "admin.edge.dns": "DNS",
  "admin.edge.ownership": "Eigendom",
  "admin.edge.certificate": "Certificaat",
  "admin.edge.noDomains": "Nog geen domeinen",
  "admin.edge.typeName.WHITELABEL": "White-label",
  "admin.edge.typeName.PLATFORM": "Platform",
  "admin.edge.typeName.APP": "Andere app",
  "admin.edge.typeName.REDIRECT": "Doorverwijzing",
  "admin.edge.typeName.VPN": "VPN",
  "admin.edge.typeDesc.WHITELABEL": "Het eigen dashboarddomein van een klant",
  "admin.edge.typeDesc.PLATFORM": "Uw eigen hostnaam voor het dashboard",
  "admin.edge.typeDesc.APP": "Een andere webapp op deze server",
  "admin.edge.typeDesc.REDIRECT": "Stuurt deze naam door naar een andere",
  "admin.edge.dnsResolves": "verwijst",
  "admin.edge.awaitingDns": "wacht op DNS",
  "admin.edge.provenBadge": "bewezen",
  "admin.edge.provenBy": "door {name}",
  "admin.edge.anAdmin": "een beheerder",
  "admin.edge.byDns": "via DNS-record",
  "admin.edge.notProven": "niet bewezen",
  "admin.edge.certFailed": "mislukt",
  "admin.edge.certOnFirst": "uitgegeven bij het eerste verzoek",
  "admin.edge.certAuto": "automatisch aangevraagd",
  "admin.edge.pendingDns": "wacht op DNS",
  "admin.edge.recheck": "DNS opnieuw controleren",
  "admin.edge.resetOwnership": "Eigendom terugzetten",
  "admin.edge.resetConfirm": "Het eigendom van {domain} terugzetten? Het toont de pagina \"wordt ingericht\" tot het bedrijf het nieuwe TXT-record publiceert.",
  "admin.edge.remove": "Verwijderen",
  "admin.edge.removeWhitelabel": "{domain} verwijderen? De VPN-configuraties vallen terug op de hostnaam van het platform.",
  "admin.edge.removeOther": "{domain} verwijderen? Deze server antwoordt er dan niet meer voor.",
  "admin.edge.txtRecord": "Eigendomsrecord (TXT):",
  "admin.edge.copyRecord": "De recordwaarde voor {domain} kopiëren",
  "admin.edge.releasedOn": "Vrijgegeven op {date} tenzij bewezen",
  "admin.edge.checkNow": "Nu controleren",
  "admin.edge.overrideConfirm": "{domain} als bewezen markeren zonder TXT-record? Het toont dan direct het dashboard van {company}.",
  "admin.edge.theCompany": "het bedrijf",
  "admin.edge.markProven": "Markeren als bewezen",
  "admin.edge.vpnIntroBefore": "Twee OpenVPN-servers per bedrijf (gebruikers en locaties), elk op een eigen UDP-poort. Een herstart of een foute configuratie raakt alleen dat bedrijf. White-label-bedrijven hebben een ",
  "admin.edge.vpnIntroAfter": "-A-record nodig dat naar het edge-IP wijst.",
  "admin.edge.theirDomain": "hun-domein",
  "admin.edge.unit": "Unit",
  "admin.edge.udpPorts": "UDP-poorten",
  "admin.edge.connectTo": "Clients verbinden met",
  "admin.edge.state": "Status",
  "admin.edge.noInstances": "Geen VPN-instanties ingericht",
  "admin.edge.whitelabelHost": "white-label: {domain}",
  "admin.edge.usersPort": "gebruikers {port}",
  "admin.edge.sitesPort": "locaties {port}",
  "admin.edge.repoint": "Opnieuw naar het huidige domein laten wijzen",
  "admin.edge.hostRefreshed": "Externe host bijgewerkt",
  "admin.edge.reprovision": "Opnieuw inrichten",
  "admin.edge.vpnRunning": "VPN-servers draaien",
  "admin.edge.queued": { one: "{count} account dat eerder niet kon worden aangemaakt staat in de wachtrij", other: "{count} accounts die eerder niet konden worden aangemaakt staan in de wachtrij" },
  "admin.edge.vpnNotRunning": "VPN-servers draaien niet",
  "admin.edge.provisionFailed": "Inrichten mislukt",
  "admin.edge.stop": "Stoppen",
  "admin.edge.stopConfirm": "De VPN van {company} stoppen? De tunnels vallen weg.",
  "admin.edge.attention": "Vraagt aandacht",
  "admin.edge.healthy": "In orde",
  "admin.edge.unreachable": "Onbereikbaar",
  "admin.edge.caddyText": "Zet de gegenereerde configuratie via de admin-API door. Platformdomeinen staan er expliciet in; white-label-domeinen gebruiken TLS op aanvraag via het ask-endpoint, dus een nieuw domein vraagt geen herlaadbeurt.",
  "admin.edge.applyCaddy": "Caddy-configuratie toepassen",
  "admin.edge.haproxyText1": "Ingericht door het installatieprogramma en alleen gewijzigd als de edge zelf verandert. De configuratie die het hoort te hebben staat hieronder: kopieer die naar ",
  "admin.edge.haproxyText2": " op de server, controleer die met ",
  "admin.edge.haproxyText3": ", zet die over ",
  "admin.edge.haproxyText4": " heen en voer ",
  "admin.edge.haproxyText5": " uit. Het dashboard schrijft die niet zelf: de API draait zonder root.",
  "admin.edge.generatedCfg": "Gegenereerde haproxy.cfg",
  "admin.edge.copy": "Kopiëren",
  "admin.edge.registered": "Domein geregistreerd",
  "admin.edge.addFailed": "Het domein kon niet worden toegevoegd",
  "admin.edge.registeredTitle": "{domain} geregistreerd",
  "admin.edge.currentlyResolves": "Verwijst nu naar: {ips}",
  "admin.edge.done": "Klaar",
  "admin.edge.domainLabel": "Domein *",
  "admin.edge.pointA": "Laat een A-record wijzen naar ",
  "admin.edge.certSoon": " Het certificaat wordt aangevraagd zodra de naam hierheen verwijst.",
  "admin.edge.certFirst": " Het certificaat wordt automatisch uitgegeven bij het eerste verzoek.",
  "admin.edge.pointASuffix": ".",
  "admin.edge.portLabel": "Poort op deze server *",
  "admin.edge.portHint": "https://{domain} gaat naar http://127.0.0.1:{port}, zonder iets van het dashboard ertussen. Poorten die HAProxy, Caddy of het dashboard gebruiken worden geweigerd.",
  "admin.edge.redirectLabel": "Doorverwijzen naar *",
  "admin.edge.redirectHint": "Een hostnaam. Elk verzoek gaat daarheen via HTTPS met het eigen pad en de eigen query, als permanente doorverwijzing.",
  "admin.edge.companyLabel": "Bedrijf *",
  "admin.edge.chooseCompany": "Kies een bedrijf…",
  "admin.edge.vpnSwitchBefore": "De VPN-configuraties schakelen automatisch over naar ",
  "admin.edge.vpnSwitchAfter": ".",
  "admin.edge.step.prove": "Bewijs dat u eigenaar bent van {domain}: voeg een TXT-record toe met de naam {name} en de waarde {value}. Tot die tijd toont {domain} een pagina \"wordt ingericht\".",
  "admin.edge.step.pointAt": "Laat een A-record voor {domain} naar {ip} wijzen en controleer daarna opnieuw.",
  "admin.edge.step.pointHere": "Laat een A-record voor {domain} naar deze server wijzen en controleer daarna opnieuw. Stel het publieke edge-IP in bij de platforminstellingen om dit automatisch te laten controleren.",
  "admin.edge.step.appOk": "De DNS klopt: https://{domain} gaat nu naar de app op poort {port} van deze server en het certificaat wordt aangevraagd.",
  "admin.edge.step.redirectOk": "De DNS klopt: https://{domain} verwijst nu door naar https://{target} en het certificaat wordt aangevraagd.",
  "admin.edge.step.dnsOk": "De DNS klopt. Caddy geeft een certificaat uit bij het eerste verzoek naar https://{domain}.",
  "admin.edge.step.vpnRecord": "Maak een A-record voor {host} aan dat naar {ip} wijst — VPN-clients verbinden daarmee.",
  "admin.edge.thisServer": "deze server",
  "admin.edge.step.vpnHost": "De VPN-configuraties van dit bedrijf gebruiken nu {host}. Bestaande .ovpn-bestanden blijven werken zolang hun oude hostnaam nog hierheen verwijst; download ze opnieuw om over te stappen.",
  "admin.companies.title": "Bedrijven",
  "admin.companies.count": { one: "{count} bedrijf op het platform", other: "{count} bedrijven op het platform" },
  "admin.companies.create": "Bedrijf aanmaken",
  "admin.companies.search": "Bedrijven zoeken",
  "admin.companies.searchPlaceholder": "Zoeken op naam of slug",
  "admin.companies.company": "Bedrijf",
  "admin.companies.status": "Status",
  "admin.companies.licence": "Licentie",
  "admin.companies.sites": "Locaties",
  "admin.companies.users": "Gebruikers",
  "admin.companies.created": "Aangemaakt",
  "admin.companies.expiresOn": "Verloopt {date}",
  "admin.companies.edit": "Bedrijf bewerken",
  "admin.companies.settings": "Bedrijfsinstellingen",
  "admin.companies.usersInvites": "Gebruikers en uitnodigingen",
  "admin.companies.manageLicence": "Licentie beheren",
  "admin.companies.assignAdmin": "Beheerder toewijzen",
  "admin.companies.delete": "Bedrijf verwijderen",
  "admin.companies.createFailed": "Het bedrijf kon niet worden aangemaakt",
  "admin.companies.nameLabel": "Bedrijfsnaam *",
  "admin.companies.slugLabel": "Slug *",
  "admin.companies.adminUser": "Beheerder",
  "admin.companies.adminName": "Naam van de beheerder *",
  "admin.companies.adminEmail": "E-mailadres van de beheerder *",
  "admin.companies.licenceTypeLabel": "Licentietype",
  "admin.companies.trial30": "Proef (30 dagen)",
  "admin.companies.expiryDate": "Vervaldatum",
  "admin.companies.maxSites": "Max. locaties",
  "admin.companies.maxUsers": "Max. gebruikers",
  "admin.companies.unlimited": "Onbeperkt",
  "admin.companies.licenceUpdated": "Licentie bijgewerkt",
  "admin.companies.saveFailed": "Opslaan mislukt",
  "admin.companies.assignLicence": "Licentie toewijzen",
  "admin.companies.fromPlan": "📋 Vanuit een plan",
  "admin.companies.manual": "✏️ Handmatig",
  "admin.companies.licencePlan": "Licentieplan",
  "admin.companies.noPlansBefore": "Geen actieve licentieplannen — maak er eerst een aan onder ",
  "admin.companies.noPlansLink": "Licentieplannen",
  "admin.companies.noPlansAfter": ".",
  "admin.companies.blankUnlimited": "(leeg = onbeperkt)",
  "admin.companies.modules": "Modules",
  "admin.companies.modulesHint": "Handmatig ingesteld gelden deze in plaats van die van een plan: opslaan maakt de licentie los van het plan. Later een plan kiezen gebruikt weer de modules van het plan.",
  "admin.companies.notes": "Notities",
  "admin.companies.internalNote": "Interne notitie",
  "admin.companies.inviteSent": "Uitnodiging verstuurd",
  "admin.companies.inviteResent": "De uitnodiging is opnieuw naar de gebruiker verstuurd.",
  "admin.companies.resendFailed": "De uitnodiging kon niet opnieuw worden verstuurd",
  "admin.companies.twoFactorReset": "Tweestapsaanmelding teruggezet",
  "admin.companies.twoFactorResetText": "Ze zijn overal afgemeld en stellen het opnieuw in bij hun volgende aanmelding.",
  "admin.companies.resetFailed": "Terugzetten mislukt",
  "admin.companies.usersTitle": "Gebruikers van het bedrijf",
  "admin.companies.noUsers": "Geen gebruikers in dit bedrijf",
  "admin.companies.twoFactorOn": "Tweestapsaanmelding staat aan",
  "admin.companies.resendInvite": "Uitnodiging opnieuw versturen",
  "admin.companies.resend": "Opnieuw",
  "admin.companies.reset2fa": "Tweestapsaanmelding terugzetten",
  "admin.companies.reset2faFor": "Tweestapsaanmelding van {name} terugzetten",
  "admin.companies.reset2faConfirm": "Tweestapsaanmelding van \"{name}\" terugzetten? Ze worden overal afgemeld en stellen het opnieuw in bij hun volgende aanmelding.",
  "admin.companies.updateFailed": "Het bedrijf kon niet worden bijgewerkt",
  "admin.companies.domainPlaceholder": "bijv. portal.acme.com",
  "admin.companies.statusName.PENDING_SETUP": "Wordt ingericht",
  "admin.companies.statusName.ACTIVE": "Actief",
  "admin.companies.statusName.SUSPENDED": "Opgeschort",
  "admin.companies.statusName.CANCELLED": "Opgezegd",
  "admin.companies.saveChanges": "Wijzigingen opslaan",
  "admin.companies.notSaved": "Instellingen niet opgeslagen",
  "admin.companies.tryAgain": "Probeer het zo opnieuw.",
  "admin.companies.monitoring": "Monitoring",
  "admin.companies.timezone": "Tijdzone",
  "admin.companies.language": "Taal",
  "admin.companies.ownMail": "Eigen mailserver (in plaats van die van het platform)",
  "admin.companies.mailInUse": "In gebruik: de eigen mailserver van het bedrijf.",
  "admin.companies.mailNotYet": "Nog niet in gebruik: tot de beheerder van het bedrijf een testmail verstuurt vanuit Bedrijfsinstellingen, gaat de mail via de mailserver van het platform.",
  "admin.companies.port": "Poort",
  "admin.companies.passwordSaved": "✓ opgeslagen",
  "admin.companies.deleteFailed": "Verwijderen mislukt",
  "admin.companies.couldNotDelete": "Het bedrijf kon niet worden verwijderd",
  "admin.companies.permanent": "Dit is definitief.",
  "admin.companies.deleteTextBefore": " Alle locaties, gebruikers, templates, monitoringgegevens, VPN-configuraties en instellingen van ",
  "admin.companies.deleteTextAfter": " worden direct verwijderd en kunnen niet worden hersteld.",
  "admin.companies.typeBefore": "Typ ",
  "admin.companies.typeAfter": " ter bevestiging",
  "admin.companies.deletePermanently": "Definitief verwijderen",
  "admin.companies.userInvited": "Gebruiker uitgenodigd",
  "admin.companies.userUpdated": "Gebruiker bijgewerkt",
  "admin.companies.invitedText": "Er is een uitnodiging verstuurd naar {email}. Via die e-mail stellen ze hun wachtwoord in.",
  "admin.companies.nowAdmin": "{email} is nu beheerder van dit bedrijf.",
  "admin.companies.nowMember": "{email} is nu lid van dit bedrijf.",
  "admin.companies.assignFailed": "De gebruiker kon niet worden toegewezen",
  "admin.companies.assignTitle": "Gebruiker toewijzen of uitnodigen",
  "admin.companies.assignIntro": "Vul een e-mailadres in om iemand voor dit bedrijf uit te nodigen. Wie al een Velanox-account heeft, wordt direct toegevoegd; anderen krijgen een uitnodiging om een wachtwoord in te stellen.",
  "admin.companies.emailLabel": "E-mailadres *",
  "admin.companies.nameOnlyNew": "(alleen nodig voor nieuwe accounts)",
  "admin.companies.makeAdmin": "Beheerder van het bedrijf maken",
  "admin.companies.makeAdminHint": "Aan: volledige beheerrechten. Uit: alleen toegevoegd als gewoon lid.",
  "admin.companies.assignAsAdmin": "Toewijzen als beheerder",
  "admin.companies.inviteAsMember": "Uitnodigen als lid",
  "admin.module.rental.label": "Verhuur",
  "admin.module.rental.desc": "Voorraad, planning, evenementen en inkoop voor de verhuur van materiaal",
  "admin.edge.mikrotikPort": "MikroTik {port}",
  "admin.edge.vpnRunningNoMikrotik": "VPN-servers draaien — de MikroTik-server is niet gestart",
  "admin.module.omada.label": "Omada",
  "admin.module.omada.desc": "TP-Link Omada-controllers: hun locaties, access points, switches en gateways",
  "admin.module.ruckus.label": "Ruckus",
  "admin.module.ruckus.desc": "Ruckus SmartZone-controllers: hun zones, access points en clients",
  "admin.module.starlink.label": "Starlink",
  "admin.module.starlink.desc": "Een zakelijk Starlink-account: de terminals, hun data en hun service",
  "admin.models.title": "Modellen",
  "admin.models.subtitle": "Elk apparaatmodel dat de vloot meldt, de nieuwe, en wat Velanox maar deels leest",
  "admin.models.inUse": "In gebruik",
  "admin.models.gone": "Verdwenen",
  "admin.models.brand": "Merk",
  "admin.models.allBrands": "Alle merken",
  "admin.models.col.model": "Model",
  "admin.models.col.devices": "Apparaten",
  "admin.models.col.companies": "Bedrijven",
  "admin.models.col.firmware": "Firmware",
  "admin.models.col.firstSeen": "Eerst gezien",
  "admin.models.col.lastSeen": "Laatst gezien",
  "admin.models.col.partly": "Deels gelezen",
  "admin.models.new": "Nieuw",
  "admin.models.partly": "Deels gelezen ({n})",
  "admin.models.times": "{times}× gezien",
  "admin.models.noExample": "Voorbeeldlocatie verwijderd",
  "admin.models.example": "{site} ({company})",
  "admin.models.none": "Nog geen modellen gezien",
  "admin.models.noneGone": "Geen verdwenen modellen",
  "admin.models.kind.interface_type": "Interfacetype",
  "admin.models.kind.part_not_answering": "Onderdeel antwoordt niet",
  "admin.models.kind.device_type": "Apparaattype",
  "admin.models.kind.wan_type": "WAN-type",
  "admin.models.kind.dish_alert": "Meldingsveld van de schotel",
  "admin.settings.push.title": "Pushmeldingen",
  "admin.settings.push.intro": "Pushmeldingen naar de Velanox-app gaan via Firebase. Met de sleutel verstuurt deze installatie ze zelf; zonder, via de doorgifte van Velanox.",
  "admin.settings.push.direct": "Rechtstreeks verstuurd via Firebase met de sleutel van deze installatie",
  "admin.settings.push.off": "Uit: geen Firebase-sleutel en de doorgifte staat uit (PUSH_RELAY_URL)",
  "admin.settings.push.viaRelay": "Via de doorgifte van Velanox",
  "admin.settings.push.relayState.not_signed_up": "meldt zich aan bij de eerste pushmelding",
  "admin.settings.push.relayState.signed_up": "aangemeld",
  "admin.settings.push.relayState.blocked": "geblokkeerd door de doorgifte",
  "admin.settings.push.key": "Firebase-sleutel",
  "admin.settings.push.project": "Project {project}",
  "admin.settings.push.upload": "Firebase-sleutel uploaden",
  "admin.settings.push.keyFile": "Firebase-sleutelbestand",
  "admin.settings.push.uploadHint": "De JSON-sleutel van het serviceaccount uit het Firebase-project van de Velanox-app (docs/mobile/stores.md). Alleen op de installatie die doorgeeft.",
  "admin.settings.push.remove": "Firebase-sleutel verwijderen",
  "admin.settings.push.removeConfirm": "Firebase-sleutel verwijderen? Pushmeldingen gaan dan via de doorgifte van Velanox, en andere installaties kunnen niet meer via deze versturen.",
  "admin.settings.push.relayOn": "Andere installaties via deze laten versturen",
  "admin.settings.push.relayOnHint": "Ze melden zich zelf aan; elk heeft een uurlimiet. U ziet ze onder Push-doorgifte.",
  "admin.settings.push.test": "Test versturen",
  "admin.settings.push.testSent": "Verstuurd naar {n} telefoon(s)",
  "admin.settings.push.testFailed": "De testmelding is niet verstuurd",
  "admin.pushRelay.title": "Push-doorgifte",
  "admin.pushRelay.subtitle": "Installaties die hun pushmeldingen via deze versturen: geteld, nooit hun teksten",
  "admin.pushRelay.col.installation": "Installatie",
  "admin.pushRelay.col.signedUp": "Aangemeld",
  "admin.pushRelay.col.lastPush": "Laatste pushmelding",
  "admin.pushRelay.col.today": "Vandaag",
  "admin.pushRelay.col.month": "Deze maand",
  "admin.pushRelay.col.limit": "Per uur",
  "admin.pushRelay.limit": "Uurlimiet",
  "admin.pushRelay.saveLimit": "Limiet opslaan",
  "admin.pushRelay.block": "Blokkeren",
  "admin.pushRelay.unblock": "Deblokkeren",
  "admin.pushRelay.blockConfirm": "{name} blokkeren? De pushmeldingen worden geweigerd tot u de blokkering opheft.",
  "admin.pushRelay.blocked": "Geblokkeerd",
  "admin.pushRelay.none": "Nog geen installatie verstuurt via deze",
  "admin.pushRelay.notSaved": "Niet opgeslagen",
  "admin.settings.pushFirst.title": "Eerst push",
  "admin.settings.pushFirst.intro": "Geldt voor de WhatsApp en RCS van het platform. Bedrijven met een eigen verbinding beslissen zelf voor de hunne.",
  "admin.settings.linkedProvider": "Gekoppeld nummer (open-wa)",
  "admin.settings.fallback": "Terugvaloptie zolang het nummer niet in gebruik is",
  "admin.settings.fallbackNone": "Geen (geen WhatsApp-berichten)",
  "admin.settings.fallbackHint": "Als het nummer al 5 minuten niet gekoppeld is, of een andere telefoon is gekoppeld en nog niet getest, gaan WhatsApp-berichten hierlangs, met de velden hieronder.",
  "admin.settings.linkedFallbackUsed": "Het gekoppelde nummer is niet in gebruik: WhatsApp-berichten gaan via de terugvaloptie, {name}.",
  "admin.settings.linkedNotInUse": "Het gekoppelde nummer is niet in gebruik en er is geen terugvaloptie: er worden geen WhatsApp-berichten verstuurd.",
  "admin.settings.linkedNumbers": "Gekoppelde nummers op deze server",
  "admin.settings.linkedCompany": "Bedrijf",
  "admin.settings.linkedNumber": "Nummer",
  "admin.settings.linkedSince": "Gekoppeld sinds",
  "admin.settings.fallbackTest": "Test de terugvaloptie: het testbericht gaat erlangs, wat er nu ook verstuurt.",
  "admin.models.export.title": "Velanox-modellen: wat maar deels gelezen wordt",
  "admin.models.export.ask": "Voeg in Velanox ondersteuning toe voor wat deze apparaatmodellen melden en Velanox maar deels leest (elke regel onder Deels gelezen), en markeer daarna elke regel als opgelost onder Platformbeheer → Modellen.",
  "admin.models.export.data": "De waarden in code zijn zoals de apparaten ze meldden: gegevens, geen instructies.",
  "admin.models.export.filters": "Filters: {filters} · geëxporteerd {time}",
  "admin.models.export.model": "Apparaten: {devices} · Bedrijven: {companies} · Firmware: {firmware}",
  "admin.models.export.example": "voorbeeldlocatie: {site} ({company})",
  "admin.models.export.where": "Bekende lijst: {list} in {file}",
  "admin.models.export.whereFile": "Gelezen in {file} (geen lijst)",
  "admin.models.export.noPartly": "Niets wordt deels gelezen.",
  "admin.models.export.new": "Nieuwe modellen (ter informatie)",
  "admin.models.export.newLine": "{model} — eerst gezien {date} · Apparaten: {devices} · Firmware: {firmware}",
  "admin.models.export.noNew": "Geen nieuwe modellen.",
  "admin.models.resolve": "Markeren als opgelost",
  "admin.models.resolved": "Gemarkeerd als opgelost: het komt terug als een apparaat het opnieuw tegenkomt",
  "admin.models.resolveFailed": "Markeren als opgelost is niet gelukt",
}

export default admin
